/**
 * Character and word counts for generated copy.
 *
 * These numbers are not decoration. The Concept Workbench prints them under
 * every copy option (`25 characters · 4 words · Confident, direct`) and turns
 * them destructive when a platform will truncate — so a wrong count is a wrong
 * truncation warning, which is worse than no warning at all.
 *
 * ── WHY NOT `text.length` ─────────────────────────────────────────────────
 * `String.prototype.length` counts UTF-16 code units, which is neither what a
 * person sees nor what a platform counts:
 *
 *   '👍'.length      === 2   (one surrogate pair)
 *   '👨‍👩‍👧'.length  === 8   (three emoji joined by two ZWJs)
 *   'é'.length       === 2   when written as e + U+0301 combining acute
 *
 * A caption of 40 family emoji reads as 40 characters and would be reported as
 * 320. `Intl.Segmenter` with `granularity: 'grapheme'` counts what the reader
 * counts, and is available in every runtime this repo targets (Node 20+, all
 * current browsers). The `Array.from` fallback below counts CODE POINTS — still
 * far better than code units, and it only runs where `Intl.Segmenter` is
 * genuinely absent.
 *
 * ⚠️ Platforms do not all agree with each other either: X counts most emoji as
 * 2, LinkedIn counts UTF-16 units for its hard cap. This module deliberately
 * reports the HUMAN count, because it sits next to human-facing copy, and the
 * limits it is compared against (`platform-limits.ts`) are the platform's own
 * published *display/truncation* numbers, which are stated in the same terms.
 */

/**
 * `Intl.Segmenter` is ES2022 and `tsconfig.app.json` targets ES2020, so the
 * type is declared structurally here rather than by widening the shared `lib`
 * for the whole app. The runtime feature is present everywhere this code runs
 * (Node 20+, all current browsers); only the type is missing.
 */
interface GraphemeSegmenter {
  segment(input: string): Iterable<{ segment: string }>;
}
type SegmenterCtor = new (
  locales: string | string[] | undefined,
  options: { granularity: 'grapheme' },
) => GraphemeSegmenter;

let segmenter: GraphemeSegmenter | null | undefined;

function graphemeSegmenter(): GraphemeSegmenter | null {
  if (segmenter !== undefined) return segmenter;
  try {
    const ctor = (Intl as unknown as { Segmenter?: SegmenterCtor }).Segmenter;
    segmenter =
      typeof ctor === 'function' ? new ctor(undefined, { granularity: 'grapheme' }) : null;
  } catch {
    segmenter = null;
  }
  return segmenter;
}

/** Characters as a reader counts them — one per grapheme cluster. */
export function countCharacters(text: string): number {
  if (!text) return 0;
  const seg = graphemeSegmenter();
  if (!seg) return Array.from(text).length;
  let count = 0;
  for (const _ of seg.segment(text)) count += 1;
  return count;
}

/**
 * Words the way a marketer counts them — whitespace-separated runs.
 *
 * Matches `CopyVariantCard`'s own `countWords` exactly, on purpose: the card
 * renders a word count of its own for text the user is editing live, and two
 * definitions of "word" on the same row would be a visible contradiction.
 */
export function countWords(text: string): number {
  const trimmed = text.trim();
  if (!trimmed) return 0;
  return trimmed.split(/\s+/).length;
}

export interface TextMetrics {
  characters: number;
  words: number;
}

export function measureText(text: string): TextMetrics {
  return { characters: countCharacters(text), words: countWords(text) };
}
